import type { Editor } from '@tiptap/react';
import { EditorContent } from '@tiptap/react';

import { Separator } from '@/components/ui/separator';
import { cn } from 'utils/cn';

import { LinkBubbleMenu } from './components/bubble-menu/link-bubble-menu';
import { MeasuredContainer } from './components/measured-container';
import { SectionFive } from './components/section/five';
import { SectionFour } from './components/section/four';
import { SectionOne } from './components/section/one';
import { SectionTwo } from './components/section/two';
import { useMinimalTiptapEditor } from './hooks/use-minimal-tiptap';
import './styles/index.css';

export interface MarkdownTiptapProps {
    value: string;
    onChange: (markdown: string) => void;
    placeholder?: string;
    className?: string;
    editorContentClassName?: string;
}

// Toolbar restricted to markdown-representable formatting: no underline, no colors,
// no images/mentions — anything else would be silently dropped by getMarkdown().
const MarkdownToolbar = ({ editor }: { editor: Editor }) => (
    <div className="border-border flex h-12 shrink-0 overflow-x-auto border-b p-2">
        <div className="flex w-max items-center gap-px">
            <SectionOne editor={editor} activeLevels={[2, 3, 4]} />

            <Separator orientation="vertical" className="mx-2" />

            <SectionTwo
                editor={editor}
                activeActions={['bold', 'italic', 'strikethrough', 'code', 'clearFormatting']}
                mainActionCount={3}
            />

            <Separator orientation="vertical" className="mx-2" />

            <SectionFour editor={editor} activeActions={['orderedList', 'bulletList']} mainActionCount={2} />

            <Separator orientation="vertical" className="mx-2" />

            <SectionFive
                editor={editor}
                activeActions={['codeBlock', 'blockquote', 'horizontalRule']}
                mainActionCount={0}
            />
        </div>
    </div>
);

export const MarkdownTiptapEditor = ({
    value,
    onChange,
    placeholder,
    className,
    editorContentClassName,
}: MarkdownTiptapProps) => {
    const editor = useMinimalTiptapEditor({
        value,
        onUpdate: (content) => onChange(content as string),
        output: 'markdown',
        placeholder,
    });

    if (!editor) {
        return null;
    }

    return (
        <MeasuredContainer
            as="div"
            name="editor"
            className={cn(
                'border-input min-data-[orientation=vertical]:h-72 flex h-auto w-full flex-col rounded-md border shadow-xs',
                'focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]',
                className,
            )}
        >
            <MarkdownToolbar editor={editor} />
            <EditorContent editor={editor} className={cn('minimal-tiptap-editor', editorContentClassName)} />
            <LinkBubbleMenu editor={editor} />
        </MeasuredContainer>
    );
};

MarkdownTiptapEditor.displayName = 'MarkdownTiptapEditor';

export default MarkdownTiptapEditor;
